// Adapter for the self-service password reset (FEATURE_002) — the logged-out, 3-step email-OTP flow.
// Mirrors the medical-api MedicalPasswordResetService: request → verify → confirm. Same IS_MOCK / httpClient
// pattern as the signup adapter; both the mock and the real branch throw ApiError so the page can switch on
// `err.code` regardless of branch. The BE returns the user-facing message in Korean (error.message); the page
// maps the wire `code` → its i18n label for display.

import { httpClient } from '@/api/http-client';
import { endpoints } from '@/api/endpoints';
import { ApiError, mapAxiosError } from '@/api/error';

interface ApiSuccess<T> {
  data: T;
}

const IS_MOCK = import.meta.env.VITE_USE_MOCK === 'true';

const MOCK_LATENCY_MS = 400;
const MOCK_VERIFICATION_CODE = '123456';
const MOCK_RESET_TOKEN = 'mock-reset-token';

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));
const normalizeEmail = (value: string): string => (value ?? '').trim();

// Deterministic email → step-1 error scenarios (dev/QA). Codes + Korean reconciled to the BE catalog.
const MOCK_EMAIL_SCENARIOS: Record<string, () => never> = {
  'notfound@uuh.ulsan.kr': () => {
    throw new ApiError('AUTH_RESET_NO_ACCOUNT', '등록된 계정이 없습니다. 관리자에게 문의하세요.', {
      email: 'notfound',
    });
  },
  'ratelimit@uuh.ulsan.kr': () => {
    throw new ApiError('RATE_LIMITED', '잠시 후 다시 시도해주세요.', undefined, {
      retryAfterSeconds: 60,
    });
  },
};

// Deterministic verification-code → step-2 error scenarios (dev/QA).
const MOCK_CODE_SCENARIOS: Record<string, () => never> = {
  '000000': () => {
    throw new ApiError('OTP_INVALID', '인증번호가 일치하지 않습니다.', { otp: 'mismatch' });
  },
  '999999': () => {
    throw new ApiError('OTP_EXPIRED', '인증번호가 만료되었습니다. 다시 인증번호를 받아주세요.', { otp: 'expired' });
  },
};

// ──────────────────────────────────────────────────────────────────────────────
// Step 1 — POST /api/v1/medical/auth/password-reset/request  body {email}  → 204
// ──────────────────────────────────────────────────────────────────────────────
export async function requestResetCode(email: string): Promise<void> {
  const normalized = normalizeEmail(email);
  if (IS_MOCK) {
    await delay(MOCK_LATENCY_MS);
    MOCK_EMAIL_SCENARIOS[normalized]?.();
    return; // mock: any other email "succeeds"; the demo code is 123456
  }
  try {
    await httpClient.post(endpoints.medicalStaff.passwordResetRequest, { email: normalized });
  } catch (err) {
    throw mapAxiosError(err);
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// Step 2 — POST /api/v1/medical/auth/password-reset/verify  body {email, otp}  → {resetToken}
// ──────────────────────────────────────────────────────────────────────────────
export async function verifyResetCode(email: string, code: string): Promise<{ resetToken: string }> {
  const normalized = normalizeEmail(email);
  if (IS_MOCK) {
    await delay(MOCK_LATENCY_MS);
    MOCK_CODE_SCENARIOS[code]?.();
    if (code !== MOCK_VERIFICATION_CODE) {
      throw new ApiError('OTP_INVALID', '인증번호가 일치하지 않습니다.', { otp: 'mismatch' });
    }
    return { resetToken: MOCK_RESET_TOKEN };
  }
  try {
    const { data } = await httpClient.post<ApiSuccess<{ resetToken: string }>>(
      endpoints.medicalStaff.passwordResetVerify,
      { email: normalized, otp: code },
    );
    return { resetToken: data.data.resetToken };
  } catch (err) {
    throw mapAxiosError(err);
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// Step 3 — POST /api/v1/medical/auth/password-reset/confirm
//          body {resetToken, newPassword, confirmPassword}  → 204
// ──────────────────────────────────────────────────────────────────────────────
export async function confirmReset(
  resetToken: string,
  newPassword: string,
  confirmPassword: string,
): Promise<void> {
  if (IS_MOCK) {
    await delay(MOCK_LATENCY_MS);
    if (newPassword !== confirmPassword) {
      throw new ApiError('AUTH_PASSWORD_CONFIRM_MISMATCH', '비밀번호 확인이 일치하지 않습니다.', {
        confirmPassword: 'mismatch',
      });
    }
    return; // mock: any policy-valid, matching password "succeeds"
  }
  try {
    await httpClient.post(endpoints.medicalStaff.passwordResetConfirm, {
      resetToken,
      newPassword,
      confirmPassword,
    });
  } catch (err) {
    throw mapAxiosError(err);
  }
}
